import { getRuntimeDocument } from '@imagise/shared';
import type { CanvasItem } from './static-canvas-dom-manager';
import { StaticCanvasDOMManager } from './static-canvas-dom-manager';
import type { CanvasSize, CSSDimensions } from './util';
import { CANVAS_DATA_ATTRIBUTE, setCanvasDimensions, setCSSDimensions } from './util';

const getDomDocument = (): Document | undefined => {
  try {
    return getRuntimeDocument();
  } catch {
    return undefined;
  }
};

type CanvasDOMManagerOptions = {
  allowTouchScrolling?: boolean;
  /**
   * @deprecated here only for backward compatibility
   */
  containerClass?: string;
};

export class CanvasDOMManager extends StaticCanvasDOMManager {
  upper: CanvasItem;
  container: HTMLDivElement;

  constructor(
    arg0?: string | HTMLCanvasElement,
    {
      allowTouchScrolling = false,
      containerClass = '',
    }: CanvasDOMManagerOptions = {},
  ) {
    super(arg0);
    const { el: lowerCanvasEl } = this.lower;
    const upperCanvasEl = this.createUpperCanvas();
    this.upper = { el: upperCanvasEl, ctx: upperCanvasEl.getContext('2d') };
    this.applyCanvasStyle(lowerCanvasEl, {
      allowTouchScrolling,
    });
    this.applyCanvasStyle(upperCanvasEl, {
      allowTouchScrolling,
      styles: {
        position: 'absolute',
        left: '0',
        top: '0',
      },
    });
    const container = this.createContainerElement();
    if (containerClass) {
      container.classList?.add(containerClass);
    }
    if (lowerCanvasEl.parentNode) {
      lowerCanvasEl.parentNode.replaceChild(container, lowerCanvasEl);
    }
    container.append?.(lowerCanvasEl, upperCanvasEl);
    this.container = container;
  }

  protected createUpperCanvas(): HTMLCanvasElement {
    const { el: lowerCanvasEl } = this.lower;
    const el = this.createCanvasElement();
    // we assign the same classname of the lowerCanvas
    el.className = lowerCanvasEl.className;
    // but then we remove the lower-canvas specific className
    el.classList?.remove('lower-canvas');
    // we add the specific upper-canvas class
    el.classList?.add('upper-canvas');
    el.setAttribute?.(CANVAS_DATA_ATTRIBUTE, 'top');
    if (lowerCanvasEl.style) {
      el.style.cssText = lowerCanvasEl.style.cssText;
    }
    el.setAttribute?.('draggable', 'true');
    return el;
  }

  protected createContainerElement(): HTMLDivElement {
    const runtimeDocument = getDomDocument();
    const container = (runtimeDocument
      ? (runtimeDocument.createElement('div') as HTMLDivElement)
      : ({} as HTMLDivElement));
    container.setAttribute?.(CANVAS_DATA_ATTRIBUTE, 'wrapper');
    this.setStyle(container, {
      position: 'relative',
    });
    this.makeElementUnselectable(container);
    return container;
  }

  /**
   * @private
   * @param element canvas element to apply styles on
   */
  protected applyCanvasStyle(
    element: HTMLCanvasElement,
    options: {
      allowTouchScrolling?: boolean;
      styles?: Record<string, string>;
    },
  ): void {
    const { styles, allowTouchScrolling } = options;
    this.setStyle(element, {
      ...styles,
      'touch-action': allowTouchScrolling ? 'manipulation' : 'none',
    });
    this.makeElementUnselectable(element);
  }

  override setDimensions(size: CanvasSize, retinaScaling: number): void {
    super.setDimensions(size, retinaScaling);
    const { el, ctx } = this.upper;
    setCanvasDimensions(el, ctx, size, retinaScaling);
  }

  override setCSSDimensions(size: Partial<CSSDimensions>): void {
    super.setCSSDimensions(size);
    setCSSDimensions(this.upper.el, size);
    setCSSDimensions(this.container, size);
  }

  override cleanupDOM(size: CanvasSize): void {
    const container = this.container;
    const { el: lowerCanvasEl } = this.lower;
    const { el: upperCanvasEl } = this.upper;
    super.cleanupDOM(size);
    container.removeChild?.(upperCanvasEl);
    container.removeChild?.(lowerCanvasEl);
    if (container.parentNode) {
      container.parentNode.replaceChild(lowerCanvasEl, container);
    }
  }

  override dispose(): void {
    super.dispose();
    // @ts-expect-error disposing
    delete this.upper;
    // @ts-expect-error disposing
    delete this.container;
  }

  protected override createCanvasElement(): HTMLCanvasElement {
    const runtimeDocument = getDomDocument();
    if (runtimeDocument && typeof runtimeDocument.createElement === 'function') {
      return runtimeDocument.createElement('canvas') as HTMLCanvasElement;
    }
    return {} as HTMLCanvasElement;
  }

  private makeElementUnselectable(element: HTMLElement): void {
    if (typeof element.onselectstart !== 'undefined') {
      element.onselectstart = () => false;
    }
    if (element.style) {
      element.style.userSelect = 'none';
    }
  }

  private setStyle(
    element: HTMLElement,
    styles: Record<string, string | undefined>,
  ): void {
    if (!element || !element.style) {
      return;
    }
    Object.entries(styles).forEach(([property, value]) => {
      if (!value) {
        return;
      }
      const cssProperty = property.startsWith('--')
        ? property
        : property.includes('-')
          ? property
          : property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
      element.style.setProperty(cssProperty, value);
    });
  }
}
